<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// Os prêmios (Rewards) abertos DENTRO de um modal por cima do hub Club
// Fidelidade — em vez de fechar o hub e navegar pra /user/rewards. A ROTA
// CONTINUA existindo e igual: aqui só reaproveitamos o mesmo `<UserRewards />`,
// que é autossuficiente (busca a lista no `useRewards`, tem tabs, filtros,
// paginação e o próprio modal de resgate).
//
// Estado só em memória (nada na URL), igual roletinha e cashback.
const store = useLoyaltyHubStore();
const { isRewardsOpen } = storeToRefs(store);

// O conteúdo é montado só depois do slide-up: a lista dispara o fetch no
// onMounted dela, e montar durante a animação faz a subida engasgar. Mesma
// estratégia do hub (que carrega no @after-enter).
const mounted = ref(false);

function close() {
  store.closeRewards();
}

// "Jogar Agora" do modal de resgate leva o jogador pra fora do hub. `close()`
// da store (e não `closeRewards()`) porque o hub e os sheets vivem fora da
// página roteada: fechar só este sheet deixaria o modal do Clube Fidelidade
// por cima do jogo, com o scroll do body ainda travado (ENG-815).
function handlePlayNow() {
  store.close();
}
</script>

<template>
  <LoyaltyHubSheet
    desktop-max-width="720px"
    :open="isRewardsOpen"
    title="Rewards"
    @close="close"
    @closed="mounted = false"
    @opened="mounted = true"
  >
    <!-- Wrapper meu, e NÃO `class` direto no `<UserRewards>`: aquele componente
         tem dois nós na raiz (a caixa + o BaseModal do resgate), e com raiz de
         fragmento o Vue não repassa a classe pra ninguém — ela era descartada
         em silêncio. É por aqui que o `:deep` alcança a caixa.
         `mounted` no v-if e não v-show: `UserRewards` precisa MONTAR de novo a
         cada abertura pro onMounted dela repuxar a lista (o jogador pode ter
         resgatado algo em outra aba/sessão desde a última vez). -->
    <div
      v-if="mounted"
      class="lhr-rewards"
    >
      <UserRewards
        modal-class="lhr-redeem-modal"
        @play="handlePlayNow"
      />
    </div>

    <!-- Enquanto o painel sobe, o vão fica com o esqueleto da lista em vez de
         vazio. -->
    <div
      v-else
      class="lhr-loading"
    >
      <div
        v-for="i in 6"
        :key="`lhr-skeleton-${i}`"
        class="lhr-skeleton"
      ></div>
    </div>
  </LoyaltyHubSheet>
</template>

<!-- Sem scope: o overlay do modal de resgate é TELEPORTADO pro body, então
     nenhum seletor com o data-attr deste componente o alcança.
     z-9998: por cima do sheet (9994) e do hub (9990), abaixo da roletinha
     (9999). O `!important` é porque a classe do BaseModal (`z-[1000]`) tem a
     mesma especificidade e vem depois na folha. -->
<style>
.lhr-redeem-modal {
  z-index: 9998 !important;
}
</style>

<style scoped lang="scss">
// Neutraliza o `UserBox` (`.userPageBox`) que embrulha a lista: na página de
// conta ele é o card — fundo translúcido, blur, cantos e 30px de recuo — sobre o
// fundo da rota. Dentro do painel do modal isso vira caixa dentro de caixa e
// come a largura útil.
// O alvo é o PRIMEIRO filho e não a classe: ela é de CSS module (`.userPageBox`
// vira `userPageBox_<hash>` em build) e o hash muda. O modal de resgate não cai
// aqui — ele é teleportado pro body, então não é filho deste wrapper.
// `!important` porque as regras do módulo têm a mesma especificidade e a ordem
// entre as folhas não é garantida.
.lhr-rewards :deep(> *:first-child) {
  background: transparent !important;
  backdrop-filter: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  gap: 0 !important;
}

.lhr-loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lhr-skeleton {
  @apply w-full h-20 rounded-lg bg-texts/5 border border-solid border-texts/10;
}
</style>
